import React, { useState } from 'react';
import { WeeklyTask, TaskPriority } from '../ProfitScenario/types';
import { TaskItem } from './tasks/TaskItem';
import { TaskInput } from './tasks/TaskInput';
import { TaskControls } from './tasks/TaskControls';

interface WeeklyTaskListProps {
    phaseId: string;
    weekIndex: number;
    tasks?: WeeklyTask[];
    onAddTask: (content: string, priority: TaskPriority, startDate: string, endDate: string) => void;
    onUpdateTask: (taskId: string, updates: Partial<{ content: string; priority: TaskPriority; startDate: string; endDate: string }>) => void;
    onDeleteTask: (taskId: string) => void;
    onToggleTask: (taskId: string) => void;
    onClearCompleted: () => void;
}

const priorityConfig: Record<TaskPriority, { color: string; label: string }> = {
    high: { color: '#ff4d4f', label: '高' },
    medium: { color: '#faad14', label: '中' },
    low: { color: '#52c41a', label: '低' }
};

export const WeeklyTaskList: React.FC<WeeklyTaskListProps> = ({
    phaseId,
    weekIndex,
    tasks = [],
    onAddTask,
    onUpdateTask,
    onDeleteTask,
    onToggleTask,
    onClearCompleted
}) => {
    const [isAdding, setIsAdding] = useState(false);
    const [newTaskContent, setNewTaskContent] = useState('');
    const [newTaskPriority, setNewTaskPriority] = useState<TaskPriority>('medium');
    const [newTaskStartDate, setNewTaskStartDate] = useState<string | undefined>(undefined);
    const [newTaskEndDate, setNewTaskEndDate] = useState<string | undefined>(undefined);
    const [editingTaskId, setEditingTaskId] = useState<string | null>(null);
    const [editingContent, setEditingContent] = useState('');

    const handleAddTask = () => {
        if (newTaskContent.trim() && newTaskStartDate && newTaskEndDate) {
            onAddTask(newTaskContent.trim(), newTaskPriority, newTaskStartDate, newTaskEndDate);
            setNewTaskContent('');
            setNewTaskPriority('medium');
            setNewTaskStartDate(undefined);
            setNewTaskEndDate(undefined);
            setIsAdding(false);
        }
    };

    const handleStartEdit = (task: WeeklyTask) => {
        setEditingTaskId(task.id);
        setEditingContent(task.content);
    };

    const handleSaveEdit = () => {
        if (editingTaskId && editingContent.trim()) {
            onUpdateTask(editingTaskId, { content: editingContent.trim() });
            setEditingTaskId(null);
            setEditingContent('');
        }
    };

    const handleCancelEdit = () => {
        setEditingTaskId(null);
        setEditingContent('');
    };

    const completedCount = tasks.filter(t => t.completed).length;
    const hasCompleted = completedCount > 0;

    return (
        <div>
            {/* 任务列表 - 网格布局 */}
            {tasks.length > 0 ? (
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
                    gap: 8
                }}>
                    {tasks.map(task => (
                        <TaskItem
                            key={task.id}
                            task={task}
                            editingTaskId={editingTaskId}
                            editingContent={editingContent}
                            priorityConfig={priorityConfig}
                            onToggleTask={onToggleTask}
                            handleStartEdit={handleStartEdit}
                            setEditingContent={setEditingContent}
                            handleSaveEdit={handleSaveEdit}
                            handleCancelEdit={handleCancelEdit}
                            onDeleteTask={onDeleteTask}
                            onUpdateTask={onUpdateTask}
                        />
                    ))}
                </div>
            ) : (
                <div style={{ textAlign: 'center', color: '#999', padding: '12px 0', fontSize: 12 }}>
                    暂无任务
                </div>
            )}

            {/* 添加任务区域 */}
            {isAdding ? (
                <TaskInput
                    newTaskContent={newTaskContent}
                    setNewTaskContent={setNewTaskContent}
                    newTaskPriority={newTaskPriority}
                    setNewTaskPriority={setNewTaskPriority}
                    newTaskStartDate={newTaskStartDate}
                    setNewTaskStartDate={setNewTaskStartDate}
                    newTaskEndDate={newTaskEndDate}
                    setNewTaskEndDate={setNewTaskEndDate}
                    handleAddTask={handleAddTask}
                    onCancel={() => {
                        setIsAdding(false);
                        setNewTaskContent('');
                        setNewTaskPriority('medium');
                        setNewTaskStartDate(undefined);
                        setNewTaskEndDate(undefined);
                    }}
                    priorityConfig={priorityConfig}
                />
            ) : (
                <TaskControls
                    tasksLength={tasks.length}
                    hasCompleted={hasCompleted}
                    completedCount={completedCount}
                    setIsAdding={setIsAdding}
                    onClearCompleted={onClearCompleted}
                />
            )}
        </div>
    );
};


